import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { fetchManualJson } from "../../apiServices/manualsApi";
import { useAuthoringStore } from "../../store/UseAuthorStore";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";
import { useReviewCommentStore } from "../../store/useReviewCommentStore";
import { useUserStore } from "../../store/userStore";
import BlockCanvasReviewer from "./BlockCanvasReviewer";
import ManualAuthoringHeaderReviewer from "./ManualAuthoringHeaderReviewer";
import ManualTOCReviewer from "./ManualTOCReviewer";
import ReviewCommentsPanel from "../reviewComments/ReviewCommentsPanel";
import "./ManualStructurePage.css";

export default function ManualStructurePageReviewer() {
  const { manualId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentFocusBlockId, setCommentFocusBlockId] = useState<string | null>(
    null
  );
  const [commentAutoStartToken, setCommentAutoStartToken] = useState(0);

  const user = useUserStore((s) => s.user);
  const masterAssembly = useAuthoringStore((s) => s.masterAssembly);
  const loadManual = useManualAuthoringStore((s) => s.loadManual);
  const presentManual = useManualAuthoringStore((s) => s.presentManual);
  const selectedSectionId = useManualAuthoringStore((s) => s.selectedSectionId);
  const selectedBlockId = useManualAuthoringStore((s) => s.selectedBlockId);
  const selectSection = useManualAuthoringStore((s) => s.selectSection);
  const setReviewCommentContext = useReviewCommentStore((s) => s.setContext);
  const loadReviewComments = useReviewCommentStore((s) => s.loadComments);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const parsedManualId = useMemo(() => {
    const id = Number(manualId);
    return Number.isNaN(id) ? 0 : id;
  }, [manualId]);

  const queryMasterAssemblyId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("masterAssemblyId");
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [location.search]);

  const resolvedMasterAssemblyId =
    (queryMasterAssemblyId > 0 ? queryMasterAssemblyId : 0) ||
    (masterAssembly?.masterAssemblyId ?? 0);

  useEffect(() => {
    selectSection(null);
  }, [selectSection]);

  useEffect(() => {
    if (!resolvedMasterAssemblyId) return;
    setReviewCommentContext(oem, resolvedMasterAssemblyId);
    void loadReviewComments();
  }, [
    loadReviewComments,
    oem,
    resolvedMasterAssemblyId,
    setReviewCommentContext,
  ]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!parsedManualId) {
        loadManual({ sections: [] });
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await fetchManualJson(oem, parsedManualId);
        if (!mounted) return;
        loadManual(result.manual ?? { sections: [] });
      } catch {
        if (!mounted) return;
        loadManual({ sections: [] });
        setError("Failed to load manual content.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [loadManual, oem, parsedManualId]);

  useEffect(() => {
    if (selectedBlockId) {
      setCommentFocusBlockId(null);
    }
  }, [selectedBlockId, selectedSectionId]);

  const sections = presentManual?.sections ?? [];

  const openCommentsForBlock = (blockId: string, startAddComposer = false) => {
    setCommentFocusBlockId(blockId);
    if (startAddComposer) {
      setCommentAutoStartToken(Date.now());
    }
  };

  return (
    <div className="container-fluid manual-structure-page">
      <div className="manual-authoring-header__wrap">
        <ManualAuthoringHeaderReviewer />
      </div>
      <div className="manual-structure-page__body">
        <div className="row g-3 h-100">
          <div className="col-2 border-end p-2 manual-structure-page__toc-col">
            <div className="manual-structure-page__toc-scroll">
              <ManualTOCReviewer />
            </div>
          </div>

          <div className="col-8 border-end p-3 manual-structure-page__main-col">
            <div className="manual-structure-page__main-scroll">
              {loading ? <div className="alert alert-info">Loading...</div> : null}
              {error ? <div className="alert alert-warning">{error}</div> : null}

              {!loading && !error ? (
                selectedSectionId ? (
                  <div className="d-flex justify-content-center">
                    <BlockCanvasReviewer
                      manualId={parsedManualId}
                      onOpenCommentsForBlock={openCommentsForBlock}
                    />
                  </div>
                ) : (
                  <div className="border rounded bg-white p-4">
                    {sections.length === 0 ? (
                      <div className="text-muted">
                        No sections available for this manual.
                      </div>
                    ) : (
                      <div className="text-muted">
                        Select a section from TOC to start review.
                      </div>
                    )}
                  </div>
                )
              ) : null}
            </div>
          </div>

          <div className="col-2 p-3 manual-structure-page__side-col">
            <div className="manual-structure-page__side-scroll">
              <ReviewCommentsPanel
                domain="manual"
                masterAssemblyId={resolvedMasterAssemblyId}
                manualId={parsedManualId > 0 ? parsedManualId : undefined}
                sectionId={selectedSectionId}
                blockId={commentFocusBlockId || selectedBlockId || undefined}
                autoStartAddToken={commentAutoStartToken || undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
